import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { api, formatApiErrorDetail } from "@/lib/api";
import { StampCard } from "@/components/StampCard";
import { X, Loader2, QrCode, Keyboard, CheckCircle2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { beep } from "@/lib/beep";

const SCANNER_ID = "loyalty-qr-reader";

export const ScanStampModal = ({ open, onClose, onStamped, accent = "#F97316", program }) => {
  const [mode, setMode] = useState("camera"); // camera | manual
  const [manual, setManual] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const scannerRef = useRef(null);
  const lockRef = useRef(false);

  const stamp = async (token) => {
    if (lockRef.current) return;
    lockRef.current = true;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/my/loyalty/scan", { token });
      beep(true);
      setResult(data);
      toast.success(data.reward_unlocked ? "Stamp added — reward unlocked! 🎉" : "Stamp added");
      onStamped?.();
      await stopScanner();
    } catch (err) {
      beep(false);
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setTimeout(() => (lockRef.current = false), 1200);
    } finally {
      setBusy(false);
    }
  };

  const stopScanner = async () => {
    const s = scannerRef.current;
    if (s) {
      try {
        if (s.isScanning) await s.stop();
        await s.clear();
      } catch {}
      scannerRef.current = null;
    }
  };

  useEffect(() => {
    if (!open || mode !== "camera" || result) return;
    let cancelled = false;
    const start = async () => {
      setError("");
      try {
        const scanner = new Html5Qrcode(SCANNER_ID, { verbose: false });
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 220, height: 220 } },
          (decoded) => stamp(decoded),
          () => {}
        );
        if (cancelled) await stopScanner();
      } catch (err) {
        setError("Could not access the camera. Use manual entry instead.");
        setMode("manual");
      }
    };
    start();
    return () => {
      cancelled = true;
      stopScanner();
    };
    // eslint-disable-next-line
  }, [open, mode, result]);

  if (!open) return null;

  const close = async () => {
    await stopScanner();
    setResult(null);
    setManual("");
    setError("");
    setMode("camera");
    lockRef.current = false;
    onClose();
  };

  const scanAnother = async () => {
    setResult(null);
    setManual("");
    setError("");
    lockRef.current = false;
    setMode("camera");
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" onClick={close}>
      <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-bold flex items-center gap-2"><QrCode className="h-5 w-5 text-orange-400" /> Scan to stamp</h3>
          <button data-testid="close-scan-modal" onClick={close} className="text-slate-400 hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        {result ? (
          <div className="space-y-4 fade-up">
            <div className="flex items-center gap-2 text-emerald-400 text-sm">
              <CheckCircle2 className="h-5 w-5" /> Stamp added for {result.name || result.phone}
            </div>
            <StampCard program={program} member={result} accent={accent} />
            <button data-testid="scan-another-button" onClick={scanAnother} className="w-full inline-flex items-center justify-center gap-2 rounded-xl py-3 font-semibold text-white" style={{ backgroundColor: accent }}>
              <RotateCcw className="h-4 w-4" /> Scan another
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-1 bg-slate-800 rounded-full p-1 mb-4">
              <button data-testid="scan-mode-camera" onClick={() => setMode("camera")} className={`rounded-full py-2 text-sm font-semibold transition-colors flex items-center justify-center gap-1.5 ${mode === "camera" ? "bg-orange-600 text-white" : "text-slate-400"}`}>
                <QrCode className="h-4 w-4" /> Camera
              </button>
              <button data-testid="scan-mode-manual" onClick={async () => { await stopScanner(); setMode("manual"); }} className={`rounded-full py-2 text-sm font-semibold transition-colors flex items-center justify-center gap-1.5 ${mode === "manual" ? "bg-orange-600 text-white" : "text-slate-400"}`}>
                <Keyboard className="h-4 w-4" /> Manual
              </button>
            </div>

            {mode === "camera" ? (
              <div>
                <div id={SCANNER_ID} data-testid="qr-reader" className="rounded-xl overflow-hidden bg-black aspect-square w-full" />
                <p className="text-xs text-slate-400 text-center mt-3">Point the camera at the customer's stamp-card QR.</p>
              </div>
            ) : (
              <form
                onSubmit={(e) => { e.preventDefault(); stamp(manual); }}
                className="space-y-3"
              >
                <p className="text-sm text-slate-400">Paste the card link or code from the customer's card.</p>
                <input data-testid="manual-token-input" value={manual} onChange={(e) => setManual(e.target.value)} placeholder="card link or token" className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-500" />
                <button data-testid="manual-stamp-button" type="submit" disabled={busy || !manual.trim()} className="w-full inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 rounded-xl py-3 font-semibold transition-colors">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} Add stamp
                </button>
              </form>
            )}

            {error && <p data-testid="scan-error" className="text-sm text-red-400 bg-red-950/40 border border-red-900/50 rounded-lg px-3 py-2 mt-3">{error}</p>}
          </>
        )}
      </div>
    </div>
  );
};
